import React, { useState, useEffect } from "react";
import "./NewPatientRegistration.css";

const theme = {
  primaryDark: "#1b325f", primaryBlue: "#282f81", accentCyan: "#11abea",
  bgLight: "#f8fafc", cardBg: "#ffffff", textMain: "#334155",
  textMuted: "#64748b", borderLight: "#e2e8f0", tableBorder: "#caced5"
};

const PharmaBillingB2C = ({ screenAction, triggerSave }) => {
  const today = new Date().toISOString().split('T')[0];

  const initHeader = {
    bill_number: "", bill_date: today, payment_mode: "Cash",
    patient_id: "", patient_name: "", doctor_name: "",
    total_amount: 0, total_discount: 0, net_amount: 0
  };

  const initLine = {
    medicine_id: "", medicine_name: "", unit: "Strip", batch_no: "", 
    qty: "", expiry_date: "", mrp: "", value_amount: 0, 
    discount_percent: 0, discount_amount: 0, net_amount: 0
  };

  const [header, setHeader] = useState(initHeader);
  const [lines, setLines] = useState([initLine]);
  
  const [isDisabled, setIsDisabled] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);

  // PATIENT SEARCH STATE
  const [patientSearchStr, setPatientSearchStr] = useState("");
  const [patientResults, setPatientResults] = useState([]);
  const [showPatientDrop, setShowPatientDrop] = useState(false);
  
  const [medicinesList, setMedicinesList] = useState([]);

  // --- ERP COMPACT STYLES ---
  const compactRowStyle = { display: 'flex', alignItems: 'center', marginBottom: '6px' };
  const compactLabelStyle = { width: '110px', fontSize: '12px', color: theme.textMain, fontWeight: '600' };
  const compactInputStyle = { 
    flex: 1, padding: '4px 6px', fontSize: '12px', border: 'none', 
    borderBottom: `1px solid ${theme.borderLight}`, outline: 'none', 
    backgroundColor: isDisabled ? 'transparent' : '#fcfcfc' 
  };

  const cellStyle = { border: `1px solid ${theme.tableBorder}`, padding: '0', margin: '0' };
  const cellInputStyle = { width: '100%', boxSizing: 'border-box', border: 'none', padding: '6px 4px', fontSize: '11px', backgroundColor: 'transparent', outline: 'none' };
  const readOnlyCell = { ...cellInputStyle, backgroundColor: '#f1f5f9', fontWeight: '600', color: theme.textMain };

  // --- DATA FETCHING ---
  const fetchBaseData = async () => {
    const user = JSON.parse(localStorage.getItem("user"));
    try {
      const medRes = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/medicines/all?employee_id=${user.id}`);
      const medData = await medRes.json();
      setMedicinesList(Array.isArray(medData) ? medData : []);
    } catch (e) { console.error("Error loading data", e); }
  };

  const fetchNextId = async () => {
    const user = JSON.parse(localStorage.getItem("user"));
    const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/pharma-billing/id?employee_id=${user.id}`);
    const data = await res.json();
    setHeader(prev => ({ ...prev, bill_number: data.bill_number }));
  };

  useEffect(() => { fetchBaseData(); fetchNextId(); }, []);

  // --- CALCULATE GRAND TOTALS ---
  useEffect(() => {
    let t_amt = 0, t_disc = 0, t_net = 0;
    lines.forEach(l => {
      t_amt += Number(l.value_amount) || 0;
      t_disc += Number(l.discount_amount) || 0;
      t_net += Number(l.net_amount) || 0;
    });
    setHeader(prev => ({
      ...prev,
      total_amount: t_amt.toFixed(2),
      total_discount: t_disc.toFixed(2),
      net_amount: Math.round(t_net).toFixed(2) // Standard pharmacy bills usually round off the net amount
    }));
  }, [lines]);

  // --- PATIENT SEARCH HANDLER ---
  const handlePatientSearch = async (val) => {
    setPatientSearchStr(val);
    if(val.length < 3) { setPatientResults([]); setShowPatientDrop(false); return; }
    
    const user = JSON.parse(localStorage.getItem("user"));
    try {
      const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/patient-by-id?q=${val}&employee_id=${user.id}`);
      const data = await res.json();
      setPatientResults(data);
      setShowPatientDrop(true);
    } catch(err) { console.error("Patient fetch err"); }
  };

  const selectPatient = (p) => {
    setPatientSearchStr(p.patient_id);
    setHeader(prev => ({
      ...prev,
      patient_id: p.patient_id,
      patient_name: p.name,
      doctor_name: p.doctor_name || "Self"
    }));
    setShowPatientDrop(false);
  };

  const handleHeaderChange = (e) => setHeader({ ...header, [e.target.name]: e.target.value });

  const handleLineChange = (index, field, value) => {
    const updated = [...lines];
    const row = updated[index];
    row[field] = value;

    if (field === "medicine_id") {
      const med = medicinesList.find(m => m.medicine_id === value);
      if (med) {
        row.medicine_name = med.medicine_name;
        row.mrp = med.mrp;
      }
    }

    // Auto Calculations
    const qty = Number(row.qty) || 0;
    const mrp = Number(row.mrp) || 0;
    row.value_amount = (qty * mrp).toFixed(2);

    if (field === "discount_amount") {
      row.discount_percent = row.value_amount > 0 ? ((Number(value) / row.value_amount) * 100).toFixed(2) : 0;
    } else {
      const discPct = Number(row.discount_percent) || 0;
      row.discount_amount = ((row.value_amount * discPct) / 100).toFixed(2);
    }

    row.net_amount = (row.value_amount - row.discount_amount).toFixed(2);

    updated[index] = row;
    setLines(updated);
  };

  const addRow = () => setLines([...lines, { ...initLine }]);
  const removeRow = (index) => setLines(lines.filter((_, i) => i !== index));

  // --- SEARCH & MODES ---
  const searchBill = async (text) => {
    setSearchText(text);
    if (text.length < 3) { setSearchResults([]); setShowDropdown(false); return; }
    const user = JSON.parse(localStorage.getItem("user"));
    const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/pharma-billing/search?q=${text}&employee_id=${user.id}`);
    setSearchResults(await res.json());
    setShowDropdown(true);
  };

  const selectBill = async (bill) => {
    setSearchText(`${bill.bill_number} | ${bill.patient_name}`);
    setShowDropdown(false);
    
    const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/pharma-billing/${bill.bill_number}`);
    const data = await res.json();
    
    if(data.header) {
      setHeader({ 
        ...data.header, 
        bill_date: data.header.bill_date ? data.header.bill_date.split('T')[0] : "" 
      });
      setPatientSearchStr(data.header.patient_id);
      setLines(data.details.length > 0 ? data.details.map(d => ({
        ...d, 
        expiry_date: d.expiry_date ? d.expiry_date.split('T')[0]:""
      })) : [initLine]);
    }
    
    if (screenAction === "edit") setIsDisabled(false);
    if (screenAction === "view") setIsDisabled(true);
  };

  const clearForm = async () => {
    setHeader(initHeader); setLines([initLine]); setSearchText(""); setPatientSearchStr("");
    await fetchNextId();
  };

  useEffect(() => {
    if (screenAction === "add" || screenAction === "refresh") { clearForm(); setIsDisabled(false); }
    if (screenAction === "edit") setIsDisabled(false);
    if (screenAction === "view") setIsDisabled(true);
    if (screenAction === "print") printForm();

  }, [screenAction]);

  useEffect(() => { if (triggerSave > 0 && screenAction === "save") handleSubmit(); }, [triggerSave]);

  const handleSubmit = async () => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!header.patient_name) return alert("Please enter or select a Patient Name.");
    
    const validLines = lines.filter(l => l.medicine_id !== "");
    if (validLines.length === 0) return alert("Add at least one medicine to the bill.");

    const payload = { header: { ...header, created_by: user.id }, details: validLines, isEdit: !!header.created_at };
    
    try {
      const res = await fetch("https://hmsapi.emedhatechnologies.com/master/api/api/pharma-billing", {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload)
      });
      if (res.ok) {
        alert("Pharmacy Bill Saved!");
        if (!payload.isEdit) clearForm();
      } else alert("Error saving Bill");
    } catch (err) { alert("Network Error"); }
  };


  const printForm = () => {
    if (!header.bill_number) return;
    const newWindow = window.open("", "", "width=900,height=700");
    
    const htmlContent = `
      <html><head><title>Pharmacy Bill - ${header.bill_number}</title>
      <style>
        body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 20px; font-size: 13px; color: #333; }
        .title-box { display: flex; justify-content: space-between; border-bottom: 2px solid #282f81; padding-bottom: 10px; margin-bottom: 20px; }
        .title-box h1 { margin: 0; color: #282f81; font-size: 24px; text-transform: uppercase; }
        .title-box h2 { margin: 0; color: #11abea; font-size: 18px; }
        .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px; padding: 15px; background-color: #f8fafc; border-left: 4px solid #282f81; border-radius: 4px; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 20px; font-size: 12px; }
        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
        th { background-color: #f2f4f4; color: #282f81; }
        .text-center { text-align: center; }
        .text-right { text-align: right; }
        .totals-box { width: 300px; float: right; border: 1px solid #ddd; padding: 10px; border-radius: 4px; }
        .totals-row { display: flex; justify-content: space-between; margin-bottom: 5px; }
        .grand-total { font-weight: bold; font-size: 16px; border-top: 1px solid #ccc; padding-top: 5px; color: #11abea; }
      </style></head>
      <body>
        <div class="title-box">
          <div><h1>EMEDHA CARE HOSPITAL</h1><p>Pharmacy Tax Invoice (B2C)</p></div>
          <div style="text-align:right"><h2>${header.bill_number}</h2><p>Date: ${header.bill_date}</p></div>
        </div>
        
        <div class="info-grid">
          <div>
            <strong>Patient Name:</strong> ${header.patient_name} <br/>
            <strong>Patient ID:</strong> ${header.patient_id || 'Cash Walk-in'}
          </div>
          <div>
            <strong>Consulting Doctor:</strong> ${header.doctor_name || 'Self'} <br/>
            <strong>Payment Mode:</strong> ${header.payment_mode}
          </div>
        </div>
        
        <table>
          <thead>
            <tr>
              <th class="text-center">SI</th>
              <th>Medicine Name</th>
              <th>Batch No</th>
              <th>Expiry</th>
              <th class="text-right">Qty</th>
              <th class="text-right">MRP</th>
              <th class="text-right">Disc</th>
              <th class="text-right">Net Amt</th>
            </tr>
          </thead>
          <tbody>
            ${lines.map((l, i) => `
              <tr>
                <td class="text-center">${i + 1}</td>
                <td>${l.medicine_name}</td>
                <td>${l.batch_no}</td>
                <td>${l.expiry_date}</td>
                <td class="text-right">${l.qty} ${l.unit}</td>
                <td class="text-right">₹${l.mrp}</td>
                <td class="text-right">₹${l.discount_amount}</td>
                <td class="text-right" style="font-weight:bold;">₹${l.net_amount}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <div class="totals-box">
          <div class="totals-row"><span>Total Value:</span><span>₹${header.total_amount}</span></div>
          <div class="totals-row"><span>Total Discount:</span><span>₹${header.total_discount}</span></div>
          <div class="totals-row grand-total"><span>Net Payable:</span><span>₹${header.net_amount}</span></div>
        </div>
        
        <div style="clear:both; margin-top: 80px; text-align: center; color: #777; font-size: 11px;">
          Thank you for your visit. Get well soon!
        </div>
      </body></html>
    `;
    newWindow.document.write(htmlContent); 
    newWindow.document.close();
    setTimeout(() => { newWindow.print(); newWindow.close(); }, 500);
  };


  return (
    <div className="registration-container fade-in" style={{ backgroundColor: theme.bgLight, padding: '10px 20px', minHeight: '100vh' }}>
  
      {(screenAction === "edit" || screenAction === "view") && (
            <>
        <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '10px' }}>
          <div style={{ position: 'relative', width: '350px' }}>
            <input type="text" value={searchText} placeholder="Search Bill No, Patient Name..." onChange={(e) => searchBill(e.target.value)}
              style={{ width: '100%', padding: '8px 16px', borderRadius: '4px', border: `1px solid ${theme.borderLight}`, outline: 'none', fontSize: '13px' }} />
            {showDropdown && searchResults.length > 0 && (
              <div style={{ position: 'absolute', top: '105%', left: '0', width: '100%', background: theme.cardBg, border: `1px solid ${theme.borderLight}`, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 100, maxHeight: '200px', overflowY: 'auto' }}>
                {searchResults.map(p => (
                  <div key={p.bill_number} onClick={() => selectBill(p)} style={{ padding: '8px 12px', borderBottom: `1px solid ${theme.borderLight}`, cursor: 'pointer', fontSize: '12px' }}>
                    <strong style={{ color: theme.primaryDark }}>{p.bill_number}</strong> &nbsp; | &nbsp; {p.patient_name}
                  </div>
                ))}
              </div>
            )}
          </div>
         
        </div>
         {screenAction === "view" && header.bill_number && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
            <button 
              type="button"
              onClick={() => alert(`Share Bill ${header.bill_number} via WhatsApp!`)}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#25D366', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', boxShadow: '0 2px 5px rgba(37, 211, 102, 0.3)' }}
              title="Share via WhatsApp"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path d="M13.601 2.326A7.85 7.85 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.9 7.9 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.9 7.9 0 0 0 13.6 2.326zM7.994 14.521a6.6 6.6 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.56 6.56 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592m3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.73.73 0 0 0-.529.247c-.182.198-.691.677-.691 1.654s.71 1.916.81 2.049c.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232"/>
              </svg>
              WhatsApp
            </button>
            </div>
          )}
          </>
      )}

      <div style={{ backgroundColor: '#fff', border: `1px solid ${theme.borderLight}`, borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.02)', padding: '20px' }}>
        
        {/* HEADER SECTION */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', marginBottom: '20px', paddingBottom: '15px', borderBottom: `2px solid ${theme.primaryDark}` }}>
          
          <div>
            <div style={compactRowStyle}>
              <div style={compactLabelStyle}>Bill Number:</div>
              <input type="text" name="bill_number" value={header.bill_number} disabled style={{...compactInputStyle, fontWeight: 'bold', color: theme.primaryBlue}} />
            </div>
            <div style={compactRowStyle}>
              <div style={compactLabelStyle}>Bill Date:</div>
              <input type="date" name="bill_date" value={header.bill_date} onChange={handleHeaderChange} disabled={isDisabled} style={compactInputStyle} />
            </div>
            <div style={compactRowStyle}>
              <div style={compactLabelStyle}>Payment Mode:</div>
              <select name="payment_mode" value={header.payment_mode} onChange={handleHeaderChange} disabled={isDisabled} style={compactInputStyle}>
                <option value="Cash">Cash</option><option value="Card">Card</option><option value="UPI">UPI</option>
              </select>
            </div>
          </div>

          <div>
            <div style={compactRowStyle}>
              <div style={compactLabelStyle}>Search Patient:</div>
              <div style={{ position: 'relative', flex: 1 }}>
                <input type="text" value={patientSearchStr} placeholder="Type ID, Name or Mobile..." onChange={(e) => handlePatientSearch(e.target.value)} disabled={isDisabled} style={{...compactInputStyle, width: '100%', boxSizing: 'border-box'}} />
                {showPatientDrop && patientResults.length > 0 && (
                  <div style={{ position: 'absolute', top: '105%', left: '0', width: '100%', background: theme.cardBg, border: `1px solid ${theme.borderLight}`, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 100, maxHeight: '150px', overflowY: 'auto' }}>
                    {patientResults.map(p => (
                      <div key={p.patient_id} onClick={() => selectPatient(p)} style={{ padding: '8px', borderBottom: `1px solid ${theme.borderLight}`, cursor: 'pointer', fontSize: '12px' }}>
                        <strong>{p.name}</strong> ({p.patient_id})
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div style={compactRowStyle}>
              <div style={compactLabelStyle}>Patient Name:</div>
              <input type="text" name="patient_name" value={header.patient_name} onChange={handleHeaderChange} disabled={isDisabled} placeholder="Or Type Walk-in Name" style={compactInputStyle} />
            </div>
            <div style={compactRowStyle}>
              <div style={compactLabelStyle}>Doctor:</div>
              <input type="text" name="doctor_name" value={header.doctor_name} onChange={handleHeaderChange} disabled={isDisabled} style={compactInputStyle} />
            </div>
          </div>

          {/* HEADER TOTALS BOX (Real-time View) */}
          <div style={{ padding: '10px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '4px' }}>
            <div style={{...compactRowStyle, justifyContent: 'space-between'}}>
              <span style={{ fontSize: '12px', color: '#166534' }}>Total Value:</span>
              <span style={{ fontWeight: 'bold' }}>₹{header.total_amount}</span>
            </div>
            <div style={{...compactRowStyle, justifyContent: 'space-between'}}>
              <span style={{ fontSize: '12px', color: '#166534' }}>Discount:</span>
              <span style={{ fontWeight: 'bold', color: 'red' }}>- ₹{header.total_discount}</span>
            </div>
            <div style={{...compactRowStyle, justifyContent: 'space-between', borderTop: '1px solid #bbf7d0', paddingTop: '6px', marginTop: '6px'}}>
              <span style={{ fontSize: '14px', color: '#14532d', fontWeight: 'bold' }}>Net Payable:</span>
              <span style={{ fontSize: '16px', fontWeight: 'bold', color: theme.primaryBlue }}>₹{header.net_amount}</span>
            </div>
          </div>

        </div>

        {/* LINE ITEMS DATA GRID */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <h3 style={{ margin: 0, fontSize: '15px', color: theme.primaryDark }}>Billing Items</h3>
          {!isDisabled && <button type="button" onClick={addRow} style={{ background: theme.primaryBlue, color: '#fff', border: 'none', padding: '4px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>+ Add Row</button>}
        </div>
        
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', border: `1px solid ${theme.tableBorder}` }}>
          <table style={{ width: '100%', minWidth: '1300px', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead style={{ backgroundColor: theme.primaryDark, color: '#fff' }}> 
              <tr>
                <th style={{ ...cellStyle, padding: '8px 4px', minWidth: '30px', textAlign: 'center' }}>SI</th>
                <th style={{ ...cellStyle, padding: '8px 4px', minWidth: '120px' }}>Medicine ID</th>
                <th style={{ ...cellStyle, padding: '8px 4px', minWidth: '200px' }}>Medicine Name</th>
                <th style={{ ...cellStyle, padding: '8px 4px', minWidth: '70px', textAlign: 'center' }}>Unit</th>
                <th style={{ ...cellStyle, padding: '8px 4px', minWidth: '100px' }}>Batch No</th>
                <th style={{ ...cellStyle, padding: '8px 4px', minWidth: '100px' }}>Expiry Dt</th>
                <th style={{ ...cellStyle, padding: '8px 4px', minWidth: '60px', textAlign: 'right' }}>Qty</th>
                <th style={{ ...cellStyle, padding: '8px 4px', minWidth: '70px', textAlign: 'right' }}>MRP</th>
                <th style={{ ...cellStyle, padding: '8px 4px', minWidth: '80px', textAlign: 'right', color: '#cbd5e1' }}>Value</th>
                <th style={{ ...cellStyle, padding: '8px 4px', minWidth: '60px', textAlign: 'right' }}>Disc%</th>
                <th style={{ ...cellStyle, padding: '8px 4px', minWidth: '70px', textAlign: 'right' }}>Disc Amt</th>
                <th style={{ ...cellStyle, padding: '8px 4px', minWidth: '90px', textAlign: 'right', color: '#11abea' }}>Net Amt</th>
                {!isDisabled && <th style={{ ...cellStyle, padding: '8px 4px', minWidth: '30px' }}></th>}
              </tr>
            </thead>
            <tbody>
              {lines.map((line, idx) => (
                <tr key={idx} style={{ borderBottom: `1px solid ${theme.borderLight}` }}>
                  <td style={{ ...cellStyle, textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f8fafc' }}>{idx + 1}</td>
                  
                  <td style={cellStyle}>
                    <select value={line.medicine_id} onChange={(e) => handleLineChange(idx, 'medicine_id', e.target.value)} disabled={isDisabled} style={cellInputStyle}>
                      <option value=""></option>
                      {medicinesList.map(m => <option key={m.medicine_id} value={m.medicine_id}>{m.medicine_id}</option>)}
                    </select>
                  </td>
                  
                  <td style={cellStyle}><input type="text" value={line.medicine_name} disabled style={readOnlyCell} title={line.medicine_name} /></td>
                  
                  <td style={cellStyle}>
                    <select value={line.unit} onChange={(e) => handleLineChange(idx, 'unit', e.target.value)} disabled={isDisabled} style={{...cellInputStyle, textAlign: 'center'}}>
                      <option>Strip</option><option>Box</option><option>Loose</option>
                    </select>
                  </td>
                  
                  <td style={cellStyle}><input type="text" value={line.batch_no} onChange={(e) => handleLineChange(idx, 'batch_no', e.target.value)} disabled={isDisabled} style={cellInputStyle} placeholder="Enter Batch" /></td>
                  <td style={cellStyle}><input type="date" value={line.expiry_date} onChange={(e) => handleLineChange(idx, 'expiry_date', e.target.value)} disabled={isDisabled} style={cellInputStyle} /></td>
                  
                  <td style={cellStyle}><input type="number" value={line.qty} onChange={(e) => handleLineChange(idx, 'qty', e.target.value)} disabled={isDisabled} style={{...cellInputStyle, textAlign: 'right', fontWeight: 'bold'}} /></td>
                  <td style={cellStyle}><input type="number" value={line.mrp} onChange={(e) => handleLineChange(idx, 'mrp', e.target.value)} disabled={isDisabled} style={{...cellInputStyle, textAlign: 'right'}} /></td>
                  <td style={cellStyle}><input type="text" value={line.value_amount} disabled style={{...readOnlyCell, textAlign: 'right', color: theme.textMuted}} /></td>
                  
                  <td style={cellStyle}><input type="number" value={line.discount_percent} onChange={(e) => handleLineChange(idx, 'discount_percent', e.target.value)} disabled={isDisabled} style={{...cellInputStyle, textAlign: 'right', color: 'red'}} /></td>
                  <td style={cellStyle}><input type="number" value={line.discount_amount} onChange={(e) => handleLineChange(idx, 'discount_amount', e.target.value)} disabled={isDisabled} style={{...cellInputStyle, textAlign: 'right', color: 'red'}} /></td>
                  
                  <td style={cellStyle}><input type="text" value={line.net_amount} disabled style={{...readOnlyCell, textAlign: 'right', fontWeight: 'bold', color: theme.primaryBlue}} /></td>
                  
                  {!isDisabled && <td style={{ ...cellStyle, textAlign: 'center', backgroundColor: '#fff' }}><button type="button" onClick={() => removeRow(idx)} style={{ color: 'red', border: 'none', background: 'transparent', cursor: 'pointer', fontWeight: 'bold' }}>X</button></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

      </div>
    </div>
  );
};

export default PharmaBillingB2C;